import find from 'lodash/find';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import { useEffect, useState } from 'react';

import useResource, {
  STATUS_LOADING,
  STATUS_SUCCEEDED,
  getConsolidatedState,
} from 'src/presentation/hooks/useResource';

import agentsResource from 'src/actions/agent/agentsResource';
import audiencesResource, { Audience } from 'src/actions/audiences/audiencesResource';

interface AudienceForTable extends Audience {
  updatedAtString: string;
  updatedByString: string;
}

const useFetchAudiences = () => {
  const [sortedAudiences, setSortedAudiences] = useState<AudienceForTable[]>();
  const [loadingState, setLoadingState] = useState(STATUS_LOADING);

  const {
    values: audiences,
    errors: audiencesErrors,
    loadingState: audiencesLoadingState,
  } = useResource(audiencesResource);
  const { values: agents, errors: agentsErrors, loadingState: agentsLoadingState } = useResource(agentsResource);
  const { resetErrors } = audiencesResource.actions;

  useEffect(() => {
    const loadingState = getConsolidatedState([audiencesLoadingState, agentsLoadingState]);
    setLoadingState(loadingState);
    if (loadingState !== STATUS_SUCCEEDED) {
      return;
    }
    const formattedAudiences = sortBy(
      audiences.map(audience => {
        const updatedAt = new Date(audience.updatedAt).toLocaleDateString();
        const updatedById = audience.updatedBy?.userId;
        const createdByAgent = find(agents, agent => agent.id === updatedById);
        const agentName = get(createdByAgent, 'name');

        return { ...audience, updatedAt, updatedBy: agentName };
      }),
      audience => audience.name?.toLowerCase()
    );
    setSortedAudiences(formattedAudiences);
  }, [agents, agentsLoadingState, audiences, audiencesLoadingState, setLoadingState, setSortedAudiences]);

  const errors = audiencesErrors || agentsErrors;

  return { audiences: sortedAudiences, errors, loadingState, resetErrors };
};

export default useFetchAudiences;
